import { useMemo, useState } from 'react';

import { useErrorStore } from '#lib/stores/errorStore';

import CustomColumnCell from '../components/CustomColumnCell/CustomColumnCell';
import CustomColumnHeaderMenu from '../components/CustomColumnHeaderMenu/CustomColumnHeaderMenu';
import { useDeleteCustomColumn, useStartColumnFill } from '../models/mutations';
import { useCustomColumns } from '../models/queries';

const SAMPLE_SIZE = 5;
const COLUMN_WIDTH = 220;

/**
 * Custom columns of a contact-list tab: the table column configs plus the dialog/fill state the
 * dialogs render. Every org column is shown on every list; values belong to the recipient.
 *
 * @param {Object} params
 * @param {string} params.listId
 * @param {'contactListContacts'|'contactListCompanies'} params.table
 * @param {Object[]} params.rows - Rows of the current page
 * @param {Object} params.selection - useScopedSelection result
 * @param {(row: Object) => {crmContactId?: string, crmCompanyId?: string}} params.getRecipientRef
 * @param {(row: Object) => string} params.getRowLabel
 * @returns {{columns: Object[], dialogs: Object}}
 */
export function useCustomColumnsTable({ listId, table, rows, selection, getRecipientRef, getRowLabel }) {
	const { data: customColumns = [] } = useCustomColumns();
	const showSuccess = useErrorStore((s) => s.showSuccess);
	const fillMutation = useStartColumnFill();
	const deleteMutation = useDeleteCustomColumn();
	const [editing, setEditing] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const fillScope = selection.toActionScope();
	const reportFillStarted = (column, { total }) =>
		showSuccess({
			title: `Spalte „${column.name}“ wird befüllt`,
			message: `${total.toLocaleString('de-DE')} Zeilen werden im Hintergrund befüllt. Je nach Anzahl der Firmen dauert das einige Minuten bis zu einer Stunde – die Werte erscheinen danach in der Tabelle.`,
			autoCloseDuration: 10_000,
		});
	const startFill = (column, mode) =>
		fillMutation.mutate(
			{ id: column.id, scope: { table, contextId: listId, ...fillScope }, mode },
			{ onSuccess: (result) => reportFillStarted(column, result) },
		);

	const columns = useMemo(
		() =>
			customColumns.map((column) => ({
				key: `custom-${column.id}`,
				label: column.name,
				width: COLUMN_WIDTH,
				onLayoutEdit: () => setEditing({ column }),
				headerAccessory: (
					<CustomColumnHeaderMenu
						column={column}
						onEdit={(c) => setEditing({ column: c })}
						onFill={startFill}
						onDelete={setDeleteTarget}
					/>
				),
				render: (row) => (
					<CustomColumnCell
						column={column}
						cell={row.customValues?.[column.id]}
						recipientRef={getRecipientRef(row)}
					/>
				),
			})),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- startFill reads the latest scope at click time
		[customColumns, getRecipientRef],
	);

	const refId = (row) => {
		const ref = getRecipientRef(row);
		return ref.crmContactId ?? ref.crmCompanyId;
	};
	// Explicitly selected rows are tested as chosen; otherwise the backend picks from the whole tab
	// (rows with a website first for website-based columns).
	const selectedIds = selection.isExplicitSelection ? selection.selectedIds.slice(0, SAMPLE_SIZE) : [];
	const previewScope = selectedIds.length > 0 ? { ids: selectedIds } : fillScope;
	const sampleLabels = Object.fromEntries(rows.map((row) => [refId(row), getRowLabel(row)]));

	const confirmDelete = async () => {
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
		} finally {
			setDeleteTarget(null);
		}
	};

	return {
		columns,
		dialogs: {
			listId,
			table,
			customColumns,
			editing,
			openCreate: () => setEditing({ column: null }),
			closeEditor: () => setEditing(null),
			previewScope,
			previewCount: selectedIds.length > 0 ? selectedIds.length : Math.min(SAMPLE_SIZE, selection.actionCount),
			sampleLabels,
			fillScope,
			fillCount: selection.actionCount,
			deleteTarget,
			cancelDelete: () => setDeleteTarget(null),
			confirmDelete,
			isDeleting: deleteMutation.isPending,
			reportFillStarted,
			exportOpen,
			openExport: () => setExportOpen(true),
			closeExport: () => setExportOpen(false),
		},
	};
}

/**
 * Place custom columns before the trailing actions column of a table's base columns.
 * @param {Object[]} baseColumns
 * @param {Object[]} customColumns
 * @returns {Object[]}
 */
export function withCustomColumns(baseColumns, customColumns) {
	const actionsIndex = baseColumns.findIndex((column) => column.key === 'actions');
	if (actionsIndex === -1) return [...baseColumns, ...customColumns];
	return [...baseColumns.slice(0, actionsIndex), ...customColumns, ...baseColumns.slice(actionsIndex)];
}
